import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useUsageAlerts } from "../../hooks/useUsageWarnings";
import { UsageData } from "../../types/billing.types";
import { UsageWarningBanner } from "../billing/UsageWarningBanner";
import LoadingScreen from "../LoadingScreen";
import {
    CompletionCard,
    MetricCard,
    RecentRecordCard,
} from "./dashboardComponents";
import PortalHeader from "./PortalHeader";

// ── Derive billing UsageData from the raw dashboard WS response ─────────────
function deriveUsageData(dash: any): UsageData | null {
  if (!dash) return null;
  const total =
    dash.creditsTotal ??
    dash.subscription?.credits_total ??
    dash.subscription?.creditsTotal ??
    0;
  if (!total) return null;
  const used =
    dash.creditsUsed ??
    dash.subscription?.credits_used ??
    dash.subscription?.creditsUsed ??
    0;
  const remaining = dash.creditsRemaining ?? total - used;
  const sub = dash.subscription;
  return {
    subscriptionId: "default-subscription",
    periodStartDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    ).toISOString(),
    periodEndDate:
      sub?.currentPeriodEnd ||
      new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        0,
      ).toISOString(),
    totalMonthlyCredit: total,
    creditsUsed: used,
    creditsRemaining: remaining,
    percentageUsed: total ? Math.round((used / total) * 100) : 0,
    percentageRemaining: total ? Math.round((remaining / total) * 100) : 0,
    dailyUsageBreakdown: [],
  };
}

// ── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [state, stats] = useDashboardData();

  const usageData = useMemo(
    () => deriveUsageData(state.dashboardData),
    [state.dashboardData],
  );
  const { warnings, dismissWarning } = useUsageAlerts(
    usageData,
    "default-subscription",
  );

  const handleCardClick = (filter: string, value: string) => {
    navigate(`/portal/records?${filter}=${value}`);
  };

  return (
    <div className="dashboard-container">
      <PortalHeader />

      {/* Credit Usage Warning Banner */}
      <div className="banners-wrapper">
        {warnings.length > 0 && (
          <UsageWarningBanner
            warnings={warnings}
            onDismiss={dismissWarning}
            isVisible={true}
          />
        )}
      </div>

      {/* Error Banner */}
      {state.connectionError && (
        <>
          <div className="error-banner-overlay" />
          <div className="error-banner">
            <div className="error-banner-content">
              <h3>⚠️ Connection Status</h3>
              <p>{state.connectionError}</p>
              <button
                className="btn-primary"
                onClick={() => window.location.reload()}
              >
                🔄 Refresh Page to Retry
              </button>
            </div>
          </div>
        </>
      )}

      {/* Loading State */}
      {state.isLoading && <LoadingScreen />}

      {/* Main Dashboard Content */}
      {!state.isLoading && !state.connectionError && state.dashboardData && (
        <div className="mx-auto max-w-6xl px-6 py-16">
          {/* Header */}
          <div
            className="relative mb-10 pl-5"
            data-testid="dashboard-header"
          >
            <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full" />
            <h1
              className="text-3xl font-black tracking-tight mb-1"
              data-testid="dashboard-title"
              style={{
                background:
                  "linear-gradient(135deg, #1e40af 0%, #0066cc 50%, #0891b2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Dashboard
            </h1>
            <p className="text-gray-500 text-sm font-medium">
              Welcome back! 👋 Here's your CDD summary
            </p>
          </div>

          {/* Metrics Grid */}
          <div className="metrics-grid" data-testid="metrics-grid">
            <MetricCard
              icon="📊"
              title="Total Records"
              value={stats.cddTotal}
              label={`${stats.cddCompleted} completed`}
              theme="blue"
              testId="metric-card-total"
              onClick={() => handleCardClick("status", "all")}
            />
            <CompletionCard
              completed={stats.cddCompleted}
              total={stats.cddTotal}
              onClick={() => handleCardClick("status", "completed")}
            />
            <MetricCard
              icon="⏳"
              title="Pending"
              value={stats.cddPending}
              label="Action needed"
              theme="amber"
              testId="metric-card-pending"
              onClick={() => handleCardClick("status", "pending")}
            />
            <MetricCard
              icon="❌"
              title="Withdrawn"
              value={stats.cddFailed}
              label="Review required"
              theme="red"
              testId="metric-card-failed"
              onClick={() => handleCardClick("status", "failed")}
            />
          </div>

          {/* Recent Records */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full" />
              <h2
                className="text-xl font-bold text-gray-900 tracking-tight"
                data-testid="recent-records-title"
              >
                Records
              </h2>
            </div>
            <div
              className="records-container"
              data-testid="records-container"
            >
              {stats.cddRecent.length > 0 ? (
                stats.cddRecent.map((rec, idx) => (
                  <RecentRecordCard key={rec.id || idx} record={rec} />
                ))
              ) : (
                <div className="empty-state">
                  <p className="empty-icon">📭</p>
                  <h3 className="empty-title">No records yet</h3>
                  <p className="empty-text">
                    Start by adding your first CDD record
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default Dashboard;
